/**
 * ProjectsGallery Component
 * 
 * A gallery component for selecting projects.
 * Displays all user projects and returns the selected project's ID.
 * 
 * Features:
 * - Display all user projects
 * - Click to select a project
 * - Returns project ID on selection
 * - Loading and error states
 * - Responsive grid layout
 * 
 * Props:
 *   - userId: Current user's unique ID
 *   - onSelect: Callback receiving one project ID or an array in multi-select mode
 *   - onClose: Optional callback to close the gallery
 *   - multiSelect: Keep the menu open and allow several projects to be selected
 *   - initialSelectedIds: Project IDs selected when multi-select mode opens
 */

import React, { useEffect, useState } from 'react';
import { loadUserProjects } from './utilities/projectsUtils.js';
import ProjectImageTile from './ProjectImageTile';
import FixedMenu from '../../Common/Menus/FixedMenu.jsx';

const EMPTY_SELECTED_IDS = Object.freeze([]);

const normalizeProjectUrl = (value = '') => {
  const trimmed = typeof value === 'string' ? value.trim() : '';
  if (!trimmed) {
    return '';
  }

  if (/^https?:\/\//i.test(trimmed)) {
    return trimmed;
  }

  return `https://${trimmed}`;
};

const ProjectsGallery = ({
  userId,
  onSelect,
  onClose,
  multiSelect = false,
  initialSelectedIds = EMPTY_SELECTED_IDS,
}) => {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedProjectIds, setSelectedProjectIds] = useState([]);

  /**
   * Load projects from Firebase
   */
  const loadProjects = async () => {
    setLoading(true);
    setError(null);

    try {
      const userProjects = await loadUserProjects(userId);
      setProjects(userProjects || []);
    } catch (err) {
      console.error('Error fetching projects:', err);
      setError('Failed to load projects');
      setProjects([]);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Load projects on component mount or when userId changes
   */
  useEffect(() => {
    if (userId) {
      loadProjects();
    }
  }, [userId]);

  useEffect(() => {
    if (!multiSelect) {
      setSelectedProjectIds((previous) => (previous.length ? [] : previous));
      return;
    }

    setSelectedProjectIds((previous) => {
      const next = Array.isArray(initialSelectedIds) ? initialSelectedIds : [];
      const unchanged = previous.length === next.length
        && previous.every((projectId, index) => projectId === next[index]);
      return unchanged ? previous : next;
    });
  }, [multiSelect, initialSelectedIds]);

  /**
   * Handle project selection
   * Returns the project ID to the parent component
   */
  const handleSelectProject = (projectId) => {
    if (multiSelect) {
      setSelectedProjectIds((previous) => previous.includes(projectId)
        ? previous.filter((selectedId) => selectedId !== projectId)
        : [...previous, projectId]);
      return;
    }

    onSelect(projectId);
    onClose?.();
  };

  const applySelection = () => {
    onSelect(selectedProjectIds);
    onClose?.();
  };

  const getFirstProjectImage = (project) => {
    if (!Array.isArray(project?.images) || project.images.length === 0) {
      return null;
    }

    return project.images[0];
  };

  const getImageUrl = (imageItem) => {
    if (!imageItem) {
      return null;
    }

    if (typeof imageItem === 'string') {
      return imageItem;
    }

    return imageItem.url || imageItem.src || imageItem.base64 || null;
  };

  return (
    <FixedMenu
      title={multiSelect ? 'Select Projects' : 'Select Project'}
      iconClass="bi-collection"
      expanded
      setExpanded={(expanded) => {
        if (!expanded) onClose?.();
      }}
    >

          <div className="card-body p-4">
          {multiSelect ? (
            <div className="d-flex justify-content-between align-items-center gap-2 mb-3">
              <small className="text-muted">
                {selectedProjectIds.length} project{selectedProjectIds.length === 1 ? '' : 's'} selected
              </small>
              <button
                className="btn btn-sm btn-primary"
                type="button"
                onClick={applySelection}
                disabled={selectedProjectIds.length === 0}
              >
                Add selected
              </button>
            </div>
          ) : null}
          {loading ? (
            <div className="gallery-loading text-center py-5">
              <div className="spinner-border spinner-border-sm text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              <p className="mt-2 text-muted">Loading projects...</p>
            </div>
          ) : error ? (
            <div className="alert alert-danger mb-0">
              <small>{error}</small>
            </div>
          ) : projects.length === 0 ? (
            <div className="gallery-empty text-center py-4">
              <p className="text-muted">No projects found</p>
              <small className="text-muted d-block">Create a project first, then return to select it.</small>
            </div>
          ) : (
            <>
              <div className="d-flex justify-content-between align-items-center mb-3">
                <small className="text-muted">
                  {projects.length} project{projects.length > 1 ? 's' : ''} available
                </small>
              </div>

              <div className="row g-3">
              {projects.map((project) => {
                const firstImage = getFirstProjectImage(project);
                const firstImageUrl = getImageUrl(firstImage);
                const projectUrl = normalizeProjectUrl(project?.projectUrl || project?.linkUrl || '');
                const selected = multiSelect && selectedProjectIds.includes(project.id);

                return (
                <div key={project.id} className="col-12 col-sm-6 col-md-4 col-xl-3">
                    <div className={`card h-100 ${selected ? 'border-primary shadow-sm' : 'border'}`}>
                      {firstImage?.imageId ? (
                        <div className="card-img-top">
                          <ProjectImageTile userId={userId} imageId={firstImage.imageId} expandable={false}/>
                        </div>
                      ) : firstImageUrl ? (
                        <img
                          src={firstImageUrl}
                          alt={project.title}
                          className="card-img-top"
                          style={{ height: '140px', objectFit: 'cover' }}
                        />
                      ) : (
                        <div className="card-img-top bg-light d-flex align-items-center justify-content-center" style={{ height: '140px' }}>
                          <i className="bi bi-image text-muted fs-4"></i>
                        </div>
                      )}

                      <div className="card-body">
                        <h6 className="card-title mb-2 text-truncate">{project.title}</h6>
                        <p className="card-text text-muted text-truncate small mb-3" style={{ minHeight: '2.8rem' }}>
                          {project.overview || 'No description provided.'}
                        </p>
                        <div className="d-flex align-items-center justify-content-between">
                          <span className="badge text-bg-light border">
                            {project.images?.length || 0} image{project.images?.length === 1 ? '' : 's'}
                          </span>
                          {projectUrl ? (
                            <a
                              className="btn btn-sm btn-outline-primary themed-link-action"
                              href={projectUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              onClick={(event) => event.stopPropagation()}
                            >
                              Visit
                            </a>
                          ) : null}
                        </div>
                      </div>

                      <div className="card-footer bg-white border-top-0 pt-0">
                        <button
                          type="button"
                          className={`btn w-100 ${selected ? 'btn-primary' : 'btn-outline-secondary'}`}
                          onClick={() => handleSelectProject(project.id)}
                          title={project.title}
                          aria-label={`${selected ? 'Deselect' : 'Select'} ${project.title}`}
                          aria-pressed={selected}
                        >
                          <i className={`bi ${selected ? 'bi-check-lg' : 'bi-plus-lg'} me-2`} aria-hidden="true"></i>
                          {selected ? 'Selected' : 'Select'}
                        </button>
                      </div>
                    </div>
                </div>
                );
              })}
            </div>
            </>
          )}
          </div>
      </FixedMenu>
  );
};

export default ProjectsGallery;
